import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin } from "lucide-react";
import Logo from "./Logo";
import { newsletterSignup } from "@/lib/api";
import { openExternal } from "@/lib/openExternal";
import { SERVICE_SECTIONS } from "./Navbar";
import { useSettings } from "@/context/SettingsContext";

const SOCIALS = [
    {
        name: "Instagram",
        href: "https://www.instagram.com/acumenfsltd/",
        icon: Instagram,
    },
    {
        name: "Facebook",
        href: "https://www.facebook.com/profile.php?id=61567588136495",
        icon: Facebook,
    },
    {
        name: "LinkedIn",
        href: "https://www.linkedin.com/company/acumen-financial-services-ltd",
        icon: Linkedin,
    },
    { name: "X", href: "https://x.com/acumenfsltd", icon: null },
    { name: "TikTok", href: "https://www.tiktok.com/@acumen.financial?_r=1&_t=ZN-95nEDkwJryo", icon: null },
    {
        name: "WhatsApp",
        href: "https://wa.me/447949541996?text=Hi%20Acumen%20%E2%80%94%20I%27d%20like%20mortgage%20advice.",
        icon: null,
    },
];

function TikTokIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.88a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31Z" />
        </svg>
    );
}

function XIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
        </svg>
    );
}

function WhatsAppIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.371s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347Zm-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.36-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.83 9.83 0 0 1 6.988 2.898 9.825 9.825 0 0 1 2.892 6.994c-.003 5.45-4.437 9.884-9.885 9.884Zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488Z" />
        </svg>
    );
}

const ICON_OVERRIDES = { TikTok: TikTokIcon, X: XIcon, WhatsApp: WhatsAppIcon };

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const { settings } = useSettings();

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await newsletterSignup(email);
            toast.success("Thanks — you are on the list.");
            setEmail("");
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const hasContact = !!(settings?.phone || settings?.email || settings?.address);

    return (
        <footer
            data-testid="site-footer"
            className="relative bg-brand-surfaceAlt text-brand-text mt-24 border-t border-brand-border"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16 py-20">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-5 space-y-6">
                        <Logo variant="light" />
                        <p className="text-brand-muted text-base leading-relaxed max-w-md font-medium">
                            From first keys to future wealth. We support homeowners,
                            investors, and businesses with smart lending, protection,
                            and insurance solutions.
                        </p>
                        {hasContact ? (
                            <div className="space-y-2 text-sm text-brand-text">
                                {settings.phone && (
                                    <a href={`tel:${settings.phone}`} className="flex items-center gap-2 hover:text-brand-primary">
                                        <Phone size={14} className="text-brand-accent" /> {settings.phone}
                                    </a>
                                )}
                                {settings.email && (
                                    <a href={`mailto:${settings.email}`} className="flex items-center gap-2 hover:text-brand-primary">
                                        <Mail size={14} className="text-brand-accent" /> {settings.email}
                                    </a>
                                )}
                                {settings.address && (
                                    <div className="flex items-start gap-2">
                                        <MapPin size={14} className="text-brand-accent mt-0.5" />
                                        <span className="whitespace-pre-line">{settings.address}</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Link
                                to="/contact"
                                data-testid="footer-contact-cta"
                                className="inline-block border border-brand-accent text-brand-accent px-5 py-2.5 text-sm font-bold hover:bg-brand-accent hover:text-white transition-colors"
                            >
                                Get in touch →
                            </Link>
                        )}

                        {/* Social icons */}
                        <div>
                            <div className="text-xs uppercase tracking-[0.22em] text-brand-accent mb-3 font-bold">
                                Stay connected
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                                {SOCIALS.map((s) => {
                                    const Override = ICON_OVERRIDES[s.name];
                                    const Icon = s.icon;
                                    return (
                                        <a
                                            key={s.name}
                                            href={s.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                openExternal(s.href);
                                            }}
                                            aria-label={s.name}
                                            data-testid={`social-${s.name.toLowerCase()}`}
                                            className="h-11 w-11 rounded-full flex items-center justify-center bg-brand-surface border border-brand-border text-brand-text hover:bg-brand-primary hover:border-brand-primary hover:text-brand-primary transition-colors cursor-pointer"
                                        >
                                            {Override ? <Override size={18} /> : <Icon size={18} />}
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-3">
                        <h4 className="text-xs uppercase tracking-[0.22em] text-brand-accent mb-5 font-bold">
                            Navigate
                        </h4>
                        <ul className="space-y-3 text-brand-text font-semibold">
                            <li><Link to="/" className="link-underline">Home</Link></li>
                            <li><Link to="/services" className="link-underline">Services</Link></li>
                            <li><Link to="/about" className="link-underline">About</Link></li>
                            <li><Link to="/contact" className="link-underline">Contact</Link></li>
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <h4 className="text-xs uppercase tracking-[0.22em] text-brand-accent mb-5 font-bold">
                            Services
                        </h4>
                        <ul className="space-y-2 text-sm text-brand-muted font-medium">
                            {SERVICE_SECTIONS.slice(1).map((s) => (
                                <li key={s.id}>
                                    <Link
                                        to={`/services#${s.id}`}
                                        className="link-underline hover:text-brand-primary transition-colors"
                                    >
                                        {s.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="mt-14 pt-8 border-t border-brand-border grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                    <div>
                        <h4 className="text-xs uppercase tracking-[0.22em] text-brand-accent mb-3 font-bold">
                            Market updates
                        </h4>
                        <p className="text-sm text-brand-muted mb-3 leading-relaxed">
                            Stay informed about UK mortgage rate changes, lender
                            criteria updates and Acumen news. You can opt out at
                            any time using the unsubscribe link in every email.
                        </p>
                        <form
                            onSubmit={submit}
                            data-testid="newsletter-form"
                            className="flex gap-2 max-w-md"
                        >
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your.email@example.com"
                                data-testid="newsletter-email"
                                className="flex-1 bg-transparent border border-white/20 px-4 py-3 text-sm text-brand-text placeholder-brand-muted focus:border-brand-accent outline-none transition-colors font-medium"
                            />
                            <button
                                data-testid="newsletter-submit"
                                disabled={loading}
                                className="bg-brand-primary hover:bg-brand-primaryHover text-brand-text px-5 py-3 text-sm font-bold transition-colors disabled:opacity-60"
                            >
                                {loading ? "…" : "Join"}
                            </button>
                        </form>
                        <p className="text-xs text-brand-muted mt-2 leading-relaxed">
                            By joining, you confirm you have read and understood
                            our{" "}
                            <Link
                                to="/privacy"
                                className="underline hover:text-brand-primary"
                            >
                                Privacy Policy
                            </Link>
                            .
                        </p>
                    </div>

                    <p className="text-xs text-brand-muted md:text-right leading-relaxed font-medium">
                        © {new Date().getFullYear()} Acumen Financial Services Ltd.
                        {settings?.fca_number ? ` · FCA FRN ${settings.fca_number}` : ""}
                        {" "}· Company no. SC725584.
                    </p>
                </div>

                {/* Quick-navigate compliance buttons */}
                <div className="mt-10 pt-8 border-t border-brand-border">
                    <div className="text-xs uppercase tracking-[0.22em] text-brand-accent mb-4 font-bold">
                        Quick navigate
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Link
                            to="/disclaimer"
                            data-testid="footer-disclaimer-btn"
                            className="inline-flex items-center px-5 py-2.5 rounded-full border border-brand-border bg-brand-surface text-sm text-brand-text font-semibold hover:bg-brand-primary hover:border-brand-primary transition-colors"
                        >
                            Disclaimer
                        </Link>
                        <Link
                            to="/privacy"
                            data-testid="footer-privacy-btn"
                            className="inline-flex items-center px-5 py-2.5 rounded-full border border-brand-border bg-brand-surface text-sm text-brand-text font-semibold hover:bg-brand-primary hover:border-brand-primary transition-colors"
                        >
                            Privacy policy
                        </Link>
                        <Link
                            to="/complaints"
                            data-testid="footer-complaints-btn"
                            className="inline-flex items-center px-5 py-2.5 rounded-full border border-brand-border bg-brand-surface text-sm text-brand-text font-semibold hover:bg-brand-primary hover:border-brand-primary transition-colors"
                        >
                            Complaints
                        </Link>
                        <Link
                            to="/contact"
                            data-testid="footer-contact-btn"
                            className="inline-flex items-center px-5 py-2.5 rounded-full border border-brand-border bg-brand-surface text-sm text-brand-text font-semibold hover:bg-brand-primary hover:border-brand-primary transition-colors"
                        >
                            Contact
                        </Link>
                    </div>
                </div>

                {/* Bottom regulatory bar — exact wording supplied by client */}
                <div className="mt-10 pt-6 border-t border-brand-border space-y-4 text-sm md:text-[15px] text-brand-text/85 leading-relaxed">
                    <p data-testid="footer-disclaimer-ar">
                        <strong className="text-brand-text">Acumen Financial Services Ltd</strong>{" "}
                        (FRN: 1001151) are an appointed representative of{" "}
                        <strong className="text-brand-text">HL Partnership Limited</strong>{" "}
                        which is authorised and regulated by the Financial Conduct
                        Authority under FCA number 303397.
                    </p>
                    <p data-testid="footer-disclaimer-company">
                        Acumen Financial Services Ltd is registered in Scotland.
                        Company registration number:{" "}
                        <strong className="text-brand-text">SC725584</strong>.
                        Registered office: 2/3, 48 George Street, Glasgow, G2 1BP.
                    </p>
                    <p data-testid="footer-disclaimer-fee">
                        There may be a fee for mortgage advice. The precise amount
                        of the fee will depend on your circumstances, but it will
                        typically range from{" "}
                        <strong className="text-brand-text">£449 to £799</strong>.
                        This will be discussed and agreed with you at the earliest
                        opportunity.
                    </p>
                    <p data-testid="footer-disclaimer-uk">
                        The guidance and/or information contained within this
                        website is subject to the UK regulatory regime and is
                        therefore targeted at consumers based in the United Kingdom.
                    </p>
                    <p className="pt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2 border-t border-brand-border">
                        <span className="text-brand-muted">
                            ACUMEN FINANCIAL SERVICES LTD · © {new Date().getFullYear()} ·
                            All rights reserved.
                        </span>
                        <Link
                            to="/admin/login"
                            data-testid="footer-admin-link"
                            className="link-underline shrink-0 text-brand-muted"
                        >
                            Admin
                        </Link>
                    </p>
                </div>
            </div>
        </footer>
    );
}
